import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { injectIntl, FormattedMessage } from 'react-intl';
import { FormatPrice } from 'utils/format';
import { getStandstillCourtesyTimes } from 'redux/modules/shipper-order-new/utils';

import { withFormikField, getFormikError } from 'utils/formik';

import { withHandlers, compose } from 'recompose';

import { StyledFirstColumn, StyledFieldWrapper, StyledExtraNoWrapChild } from '../styled';

import { Spacer, Flexer, Checkbox, Field } from '@ontruck/orc';

import { splitHours } from 'utils/converters';

export const getTotalNumberOfItems = (loads) => {
  return (loads || []).reduce((acc, load) => acc + (load || {}).equivalent_pallets, 0);
};

export const getTotalWeight = (loads) => {
  return (loads || []).reduce((acc, load) => acc + (load || {}).total_weight, 0);
};

export const getDefaultTimes = async (stop) => {
  try {
    const pendingStatusObject = stop?.shipment_status?.find((statusObject) => {
      return ['PENDING_PICKUP', 'PENDING_DROPOFF'].includes(statusObject.status);
    });
    const doneStatusObject = stop?.shipment_status?.find((statusObject) => {
      return ['DELIVERED', 'LOADED'].includes(statusObject.status);
    });

    const loadsPerStopType = { PICKUP: stop?.load_pickup, DROPOFF: stop?.load_dropoff };
    const stopLoads = loadsPerStopType[stop?.stop_type];
    const totalNumberOfItems = getTotalNumberOfItems(stopLoads);
    const totalWeight = getTotalWeight(stopLoads);
    const country = stop?.address?.country;

    const response = await getStandstillCourtesyTimes({
      numberOfItems: totalNumberOfItems,
      weight: totalWeight,
      country,
    });

    const timeFrom =
      !!pendingStatusObject?.created_at &&
      moment(pendingStatusObject?.created_at)
        .add('minutes', response?.courtesy_time_minutes)
        .format('HH:mm');
    const timeTo =
      !!doneStatusObject?.created_at && moment(doneStatusObject?.created_at).format('HH:mm');

    return {
      time_from: timeFrom,
      time_to: timeTo,
    };
  } catch (e) {
    console.error(e);
  }
};

const enhance = compose(
  withHandlers({
    handleFieldChange:
      ({ stop, form, field }) =>
      (fieldName) =>
      (_, { value }) => {
        const { setFieldValue } = form;
        const { name } = field;

        setFieldValue(name, {
          ...field.value,
          [fieldName]: value,
        });
      },
    getStandstillTime:
      ({ form, field }) =>
      (t) => {
        const { value } = field;
        const { isValid } = form;
        if (!isValid) {
          return {};
        }

        const { time_from, time_to } = value || {};

        if (!time_from || !time_to) {
          return {};
        }

        const times_from = splitHours(time_from);
        const times_to = splitHours(time_to);

        const momentFrom = moment().hours(times_from.hours).minutes(times_from.minutes);
        const momentTo = moment().hours(times_to.hours).minutes(times_to.minutes);

        const fromIsValid = momentFrom && momentFrom.isValid && momentFrom.isValid();
        const toIsValid = momentTo && momentTo.isValid && momentTo.isValid();

        if (!fromIsValid || !toIsValid) {
          return {};
        }

        const time = momentTo
          .subtract(momentFrom.hours(), 'hours')
          .subtract(momentFrom.minutes(), 'minutes');
        const literal = time.format(t.time_format);
        const numberOfMinutes = time.hours() * 60 + time.minutes();

        return {
          literal,
          numberOfMinutes,
        };
      },
  }),
  withHandlers({
    handleCheckbox:
      ({ stop_id, stop, shipment_id, form, field }) =>
      async (event) => {
        const { setFieldValue } = form;
        const { name } = field;
        if (!event || !event.target) return;
        if (!event.target.checked) {
          setFieldValue(name, null);
          return;
        }

        const defaultTimes = await getDefaultTimes(stop);

        setFieldValue(name, {
          stop_id,
          shipment_id,
          ...{
            ...(defaultTimes?.time_from ? { time_from: defaultTimes.time_from } : {}),
            ...(defaultTimes?.time_to ? { time_to: defaultTimes.time_to } : {}),
          },
        });
      },
  })
);

const StandstillExtraElement = ({
  stop,
  disabled,
  disableField,
  form,
  handleCheckbox,
  handleFieldChange,
  getStandstillTime,
  field,
  currency,
  pricePerHour,
  intl,
}) => {
  const t = intl.messages.modules.form.StandStillExtraElement;
  const { value } = field;
  const checked = !!value;
  const error = getFormikError({ field, form });

  const { time_from, time_to } = value || {};
  const {
    stop_type,
    address: { company_name, label },
  } = stop;

  const standstillTime = getStandstillTime(t);
  const timeString = standstillTime.literal;
  const numberOfMinutes = standstillTime.numberOfMinutes;

  return (
    <Flexer.Parent alignItems="center" fullWidth>
      <StyledFirstColumn p="S">
        <Spacer>
          <Checkbox
            checked={checked}
            label={
              <FormattedMessage
                id="standstill_title"
                defaultMessage={t.standstill_title}
                values={{
                  stop_type: t.standstill_type[stop_type],
                  company_name,
                  label,
                }}
              />
            }
            onChange={handleCheckbox}
            disabled={disabled}
          />
        </Spacer>
      </StyledFirstColumn>
      <StyledFieldWrapper pTop="XS" pBottom="XS" visible={checked} fullWidth>
        <Flexer.Parent alignItems="center" fullWidth>
          <Flexer.Child mRight="M">
            <Field
              value={time_from}
              label={t.starts}
              width="M"
              maskedProps={{ mask: '99:99', maskChar: null }}
              onChange={handleFieldChange('time_from')}
              invalid={!!error && !!error.time_from}
              disabled={disableField}
            />
          </Flexer.Child>
          <Flexer.Child mRight="S">
            <Field
              value={time_to}
              label={t.ends}
              width="M"
              maskedProps={{ mask: '99:99', maskChar: null }}
              onChange={handleFieldChange('time_to')}
              invalid={!!error && !!error.time_to}
              disabled={disableField}
            />
          </Flexer.Child>
          {!!(timeString && typeof pricePerHour !== 'undefined') && (
            <Fragment>
              <StyledExtraNoWrapChild flex="1">
                <FormattedMessage
                  id="summary_text"
                  defaultMessage={t.summary_text}
                  values={{
                    time: timeString,
                    price: <FormatPrice currency={currency} value={pricePerHour} />,
                  }}
                />
              </StyledExtraNoWrapChild>
              <StyledExtraNoWrapChild pRight="S">
                <FormattedMessage
                  id="summary_total"
                  defaultMessage={t.summary_total}
                  values={{
                    price: (
                      <FormatPrice
                        currency={currency}
                        value={(pricePerHour / 60) * numberOfMinutes}
                      />
                    ),
                  }}
                />
              </StyledExtraNoWrapChild>
            </Fragment>
          )}
        </Flexer.Parent>
      </StyledFieldWrapper>
    </Flexer.Parent>
  );
};

StandstillExtraElement.propTypes = {
  stop: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  disableField: PropTypes.bool,
  form: PropTypes.object.isRequired,
  handleCheckbox: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  getStandstillTime: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  currency: PropTypes.object.isRequired,
  pricePerHour: PropTypes.string.isRequired,
};

StandstillExtraElement.defaultProps = {
  disabled: false,
  disableField: false,
};

export default injectIntl(withFormikField(enhance(StandstillExtraElement)));
